/* global axios */
import ApiClient from './ApiClient';
import { resourcePath, requestConfig, withQuery } from './lais/httpHelpers';
import { IMOVEIS_ENDPOINTS, VISITAS_ENDPOINTS } from './lais/endpoints';
import {
  buildReferenceVisitListParams,
  formatReferenceCreateVisitPayload,
  formatReferenceRegisterVisitPayload,
  formatReferenceRescheduleVisitPayload,
  REFERENCE_DEFAULT_CURRENT_PAGE,
  REFERENCE_DEFAULT_PAGE_SIZE,
  REFERENCE_DEFAULT_REQUESTED_FIELDS,
} from 'dashboard/routes/dashboard/visitas/utils/referenceVisitLogic';

class VisitaAPI extends ApiClient {
  constructor() {
    super('visitas', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(withQuery(this.url, params), requestConfig());
  }

  getVisitsByAgentId(
    businessContext,
    currentPage = REFERENCE_DEFAULT_CURRENT_PAGE,
    pageSize = REFERENCE_DEFAULT_PAGE_SIZE,
    requestedFields = REFERENCE_DEFAULT_REQUESTED_FIELDS,
    filters = {}
  ) {
    return this.get(
      buildReferenceVisitListParams(
        businessContext,
        currentPage,
        pageSize,
        requestedFields,
        filters
      )
    );
  }

  show(id) {
    return axios.get(resourcePath(this.url, id), requestConfig());
  }

  getVisitByCode({ visitCode, requestedFields = [] } = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, visitCode), {
        requested_fields: requestedFields,
      }),
      requestConfig()
    );
  }

  confirmVisitByCode({ onBehalfOf = 'SUPPLY', visitCode } = {}) {
    return this.update(visitCode, {
      status: 'confirmada',
      confirmed_on_behalf_of: onBehalfOf,
    });
  }

  get accountUrl() {
    return this.url.replace(/\/visitas$/, '');
  }

  agenda(params = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, VISITAS_ENDPOINTS.agenda), params),
      requestConfig()
    );
  }

  recommendations(id, params = {}) {
    return axios.get(
      withQuery(
        resourcePath(this.url, id, VISITAS_ENDPOINTS.recommendations),
        params
      ),
      requestConfig()
    );
  }

  getVisitRecommendations(visitCode, params = {}) {
    return this.recommendations(visitCode, params);
  }

  submitReviewReport(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.reviewReport),
      { review_report: payload },
      requestConfig()
    );
  }

  finish(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.finish),
      payload,
      requestConfig()
    );
  }

  checkIn(id) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.checkIn),
      {},
      requestConfig()
    );
  }

  getOutcome(id) {
    return axios.get(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.outcome),
      requestConfig()
    );
  }

  finalizeOutcome(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.finalizeOutcome),
      { visita: payload },
      requestConfig()
    );
  }

  issueFeedbackToken(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.issueFeedbackToken),
      payload,
      requestConfig()
    );
  }

  approveConfirmationAudit(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.approveConfirmationAudit),
      payload,
      requestConfig()
    );
  }

  rejectConfirmationAudit(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.rejectConfirmationAudit),
      payload,
      requestConfig()
    );
  }

  submitRequestFeedback(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.requestFeedback),
      { visita: payload },
      requestConfig()
    );
  }

  createFollowUp(visitCode, payload = {}) {
    return this.submitReviewReport(visitCode, payload);
  }

  getVisitorQualification({ userId, businessContext, houseId } = {}) {
    const path = resourcePath(this.accountUrl, 'visitor_info', userId);
    return axios.get(
      withQuery(path, {
        business_context: businessContext,
        house_id: houseId,
      }),
      requestConfig()
    );
  }

  availability(params = {}) {
    return axios.get(
      withQuery(resourcePath(this.url, VISITAS_ENDPOINTS.availability), params),
      requestConfig()
    );
  }

  updateAvailability(payload = {}) {
    return axios.put(
      resourcePath(this.url, VISITAS_ENDPOINTS.availability),
      payload,
      requestConfig()
    );
  }

  agentScheduleBlocks(params = {}) {
    const path = resourcePath(
      this.accountUrl,
      VISITAS_ENDPOINTS.agentScheduleBlocks
    );
    return axios.get(withQuery(path, params), requestConfig());
  }

  agentScheduleBlockConfig(params = {}) {
    const path = resourcePath(
      this.accountUrl,
      VISITAS_ENDPOINTS.agentScheduleBlocks,
      VISITAS_ENDPOINTS.config
    );
    return axios.get(withQuery(path, params), requestConfig());
  }

  createAgentScheduleBlock(payload = {}) {
    return axios.post(
      resourcePath(this.accountUrl, VISITAS_ENDPOINTS.agentScheduleBlocks),
      { agent_schedule_block: payload },
      requestConfig()
    );
  }

  bulkCreateAgentScheduleBlocks(payload = {}) {
    return axios.post(
      resourcePath(
        this.accountUrl,
        VISITAS_ENDPOINTS.agentScheduleBlocks,
        VISITAS_ENDPOINTS.bulk
      ),
      { agent_schedule_block: payload },
      requestConfig()
    );
  }

  updateAgentScheduleBlock(id, payload = {}) {
    return axios.patch(
      resourcePath(this.accountUrl, VISITAS_ENDPOINTS.agentScheduleBlocks, id),
      { agent_schedule_block: payload },
      requestConfig()
    );
  }

  deleteAgentScheduleBlock(id) {
    return axios.delete(
      resourcePath(this.accountUrl, VISITAS_ENDPOINTS.agentScheduleBlocks, id),
      requestConfig()
    );
  }

  agentKeys(params = {}) {
    const path = resourcePath(
      this.accountUrl,
      VISITAS_ENDPOINTS.imovelKeyHandovers,
      VISITAS_ENDPOINTS.agentKeys
    );
    return axios.get(withQuery(path, params), requestConfig());
  }

  createKeyHandover(imovelId, payload = {}, options = {}) {
    const path = resourcePath(
      this.accountUrl,
      'imoveis',
      imovelId,
      IMOVEIS_ENDPOINTS.keyHandovers
    );
    return axios.post(
      withQuery(path, options),
      { imovel_key_handover: payload },
      requestConfig()
    );
  }

  pickupKeyHandover(id, payload = {}) {
    return axios.patch(
      resourcePath(
        this.accountUrl,
        VISITAS_ENDPOINTS.imovelKeyHandovers,
        id,
        VISITAS_ENDPOINTS.pickup
      ),
      payload,
      requestConfig()
    );
  }

  notDeliveredKeyHandover(id, payload = {}) {
    return axios.patch(
      resourcePath(
        this.accountUrl,
        VISITAS_ENDPOINTS.imovelKeyHandovers,
        id,
        VISITAS_ENDPOINTS.notDelivered
      ),
      payload,
      requestConfig()
    );
  }

  returnKeyHandover(id) {
    return axios.patch(
      resourcePath(
        this.accountUrl,
        VISITAS_ENDPOINTS.imovelKeyHandovers,
        id,
        VISITAS_ENDPOINTS.return
      ),
      {},
      requestConfig()
    );
  }

  create(payload) {
    return axios.post(this.url, { visita: payload }, requestConfig());
  }

  createVisit(payload = {}) {
    return this.create(formatReferenceCreateVisitPayload(payload));
  }

  registerVisit(payload = {}) {
    return this.create(formatReferenceRegisterVisitPayload(payload));
  }

  update(id, payload) {
    return axios.patch(
      resourcePath(this.url, id),
      { visita: payload },
      requestConfig()
    );
  }

  rescheduleVisit(visitCode, payload = {}) {
    return this.update(
      visitCode,
      formatReferenceRescheduleVisitPayload(payload)
    );
  }

  reschedule(id, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, VISITAS_ENDPOINTS.reschedule),
      payload,
      requestConfig()
    );
  }

  requestVisitBatch(payload = {}) {
    const isNativeVisitPayload = visit =>
      Boolean(
        visit?.imovel_id ||
          visit?.contact_id ||
          visit?.scheduled_at ||
          visit?.additional_attributes
      );
    let visits = [];
    if (Array.isArray(payload)) {
      visits = payload;
    } else if (Array.isArray(payload.visits)) {
      visits = payload.visits;
    } else if (Array.isArray(payload.requests)) {
      visits = payload.requests.map(request => ({
        contact_id: payload.visitorId || payload.visitor_id,
        imovel_id: request.houseId || request.house_id,
        scheduled_at:
          request.scheduledAt ||
          request.scheduled_at ||
          request.dateTime ||
          request.date_time,
        status: 'agendada',
        additional_attributes: {
          source: 'extra_visits_shortcut',
          origin: payload.origin || 'Corretores',
          business_context: payload.businessContext || payload.business_context,
          on_behalf_of_role:
            payload.onBehalfOfRole || payload.on_behalf_of_role || 'DEMAND',
          source_visit_id: payload.sourceVisitId || payload.source_visit_id,
          recommendation_id:
            request.recommendationId || request.recommendation_id,
        },
      }));
    }

    if (visits.length) {
      return Promise.all(
        visits.map(visit =>
          isNativeVisitPayload(visit)
            ? this.create(visit)
            : this.createVisit(visit)
        )
      );
    }

    return this.createVisit(payload);
  }

  batchCancel(ids = [], payload = {}) {
    return axios.post(
      resourcePath(this.url, VISITAS_ENDPOINTS.batchCancel),
      { ids, visita: payload },
      requestConfig()
    );
  }
}

export default new VisitaAPI();
